'use client'

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSession } from 'next-auth/react'

import { CookieNamesEnum } from '@/enums/CookieNamesEnum'
import { RequestFieldsEnum } from '@/enums/RequestFieldsEnum'
import { UserIdEnum } from '@/enums/UserIdEnum'
import { hasCartNotFoundBody } from '@/helpers/cartNotFoundError'
import { getQueryCacheKey } from '@/helpers/getQueryCacheKey'
import { getUserId } from '@/helpers/getUserId'
import { useAsmSession } from '@/hooks/useAsmSession'
import { useCookie } from '@/hooks/useCookie'
import { useSelectedOrgUnitId } from '@/hooks/useSelectedOrgUnitId'
import { useSiteContext } from '@/hooks/useSiteContext'
import { cartCurrentClient } from '@/lib/clients/cartCurrentClient'
import { cartsClient } from '@/lib/clients/cartsClient'
import { useServerCookies } from '@/providers/SiteContextProvider'

import type { Context, PropsWithChildren } from 'react'

function useCartIdValue() {
  const serverCookies = useServerCookies()
  const [cartIdCookie, setCartId] = useCookie<string>(CookieNamesEnum.Cart, '', serverCookies)
  const cartId = cartIdCookie === '' ? null : cartIdCookie

  return useMemo(() => ({ cartId, setCartId }), [cartId, setCartId])
}

type CartIdValue = ReturnType<typeof useCartIdValue>

function useCurrentCartValue({ cartId, setCartId }: CartIdValue) {
  const queryClient = useQueryClient()
  const { data: session } = useSession()
  const { isAuthorized } = useAsmSession()
  const { getBaseSiteId } = useSiteContext()

  const baseSiteId = getBaseSiteId()
  const userId = getUserId(session)

  const cartCurrentReq = useMemo(() => ({ baseSiteId, userId, fields: RequestFieldsEnum.Full }), [baseSiteId, userId])

  const selectedOrgUnitId = useSelectedOrgUnitId()

  const cartCurrentCacheKey = useMemo(
    () => getQueryCacheKey(cartCurrentClient.getCurrentOrgCart.name, cartCurrentReq, selectedOrgUnitId),
    [cartCurrentReq, selectedOrgUnitId]
  )

  const recreateCart = async () => {
    const newCart = await cartsClient.createCart({ baseSiteId, userId })
    if (newCart.code) {
      setCartId(newCart.code)
    }

    return newCart
  }

  const getCartCurrent = ({ signal }: { signal: AbortSignal }) =>
    cartCurrentClient.getCurrentOrgCart(cartCurrentReq, { signal }).catch(async (error: unknown) => {
      if (await hasCartNotFoundBody(error)) {
        return await recreateCart()
      }

      throw error
    })

  const cartCurrentQuery = useQuery({
    queryKey: cartCurrentCacheKey,
    queryFn: getCartCurrent,
    enabled: isAuthorized,
  })

  const { isError } = cartCurrentQuery
  const currentUserCart = cartCurrentQuery.data?.code ?? null
  const isRegisteredUser = userId !== UserIdEnum.Anonymous
  const current = isRegisteredUser ? currentUserCart : cartId

  const currentCartIdRef = useRef(current)
  useLayoutEffect(() => {
    currentCartIdRef.current = current
  }, [current])

  const isCurrentCartId = useCallback(
    (candidateCartId: string | null | undefined) =>
      candidateCartId != null && candidateCartId === currentCartIdRef.current,
    []
  )

  useEffect(() => {
    if (isRegisteredUser && currentUserCart && currentUserCart !== cartId) {
      setCartId(currentUserCart)
    }
  }, [isRegisteredUser, currentUserCart, cartId, setCartId])

  const invalidateCurrentCacheQuery = useCallback(
    async () => await queryClient.invalidateQueries({ queryKey: cartCurrentCacheKey }),
    [queryClient, cartCurrentCacheKey]
  )

  const resetCurrentCart = useCallback(
    async () => await queryClient.resetQueries({ queryKey: cartCurrentCacheKey }),
    [queryClient, cartCurrentCacheKey]
  )

  const loadCurrentCartId = useCallback(async () => {
    await queryClient.refetchQueries({ queryKey: cartCurrentCacheKey, exact: true }, { cancelRefetch: false })
    const { status: loadStatus, data: currentCart } =
      queryClient.getQueryState<typeof cartCurrentQuery.data>(cartCurrentCacheKey) ?? {}

    return loadStatus === 'success' ? (currentCart?.code ?? null) : null
  }, [queryClient, cartCurrentCacheKey])

  const setCurrentCart = useCallback(
    (cart: NonNullable<typeof cartCurrentQuery.data>) => queryClient.setQueryData(cartCurrentCacheKey, cart),
    [queryClient, cartCurrentCacheKey]
  )

  return useMemo(
    () => ({
      isError,
      current,
      isCurrentCartUnknown: isRegisteredUser && currentUserCart == null,
      invalidateCurrentCacheQuery,
      isCurrentCartId,
      loadCurrentCartId,
      resetCurrentCart,
      setCurrentCart,
    }),
    [
      isError,
      current,
      isRegisteredUser,
      currentUserCart,
      invalidateCurrentCacheQuery,
      isCurrentCartId,
      loadCurrentCartId,
      resetCurrentCart,
      setCurrentCart,
    ]
  )
}

type CurrentCartValue = ReturnType<typeof useCurrentCartValue>

export const CartIdContext = createContext<CartIdValue | null>(null)
export const CurrentCartContext = createContext<CurrentCartValue | null>(null)

export function useRequiredCartContext<T>(context: Context<T | null>, hookName: string): T {
  const value = useContext(context)

  if (value === null) {
    throw new Error(`${hookName} must be used inside CurrentCartProvider`)
  }

  return value
}

export const CurrentCartProvider = ({ children }: PropsWithChildren) => {
  const cartIdValue = useCartIdValue()
  const currentCartValue = useCurrentCartValue(cartIdValue)

  return (
    <CartIdContext.Provider value={cartIdValue}>
      <CurrentCartContext.Provider value={currentCartValue}>{children}</CurrentCartContext.Provider>
    </CartIdContext.Provider>
  )
}
